import closeIcon from "../../assests/images/dashboard/Vector 3.png";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { BtnLoader } from "src/Loader";
import DropdownEB from "../Dropdown/DropdownEB";
import {
  add_table,
  edit_table,
} from "src/store/features/restuarant-table-slice";
import { useAuth } from "src/contexts/auth-provider";
import { FEATURE_NAMES } from "src/utils/isAllowedFeature";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { ADD_TABLE, UPDATE_TABLE } from "src/constants/mutations";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast } from "src/utils/toasts";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";

const TABLE_SIZE = [
  { id: "1", name: "2" },
  { id: "2", name: "4" },
  { id: "3", name: "6" },
  { id: "4", name: "8" },
  { id: "5", name: "10" },
  { id: "6", name: "12" },
];

export const ManageTableModal = ({
  setShowCreateModal,
  title,
  editTableModal,
}: any) => {
  const { hasRestaurant, permissions } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const hasAdvancedTableManagement = permissions[FEATURE_NAMES.AdvancedTableManagement];
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      tableNumber: "",
      tableSize: "",
      section: "",
    },
    validationSchema: Yup.object({
      tableNumber: Yup.string().required("Table Number is Required"),
      tableSize: Yup.string().required("Size is Required"),
      section: Yup.number()
        .transform((value, originalValue) => {
          if (
            originalValue === "" ||
            originalValue === undefined ||
            originalValue === null
          ) {
            return null;
          }
          return Number(originalValue);
        })
        .nullable()
        .min(1, "Section must be greater than 0")
        .typeError("Section must be a valid number"),
    }),
    onSubmit: () => {},
  });

  const manageTable = async () => {
    setLoading(true);
    try {
      const tableNumberValue = Number(formik.values.tableNumber);
      const tableSizeValue = Number(formik.values.tableSize);

      if (Number.isNaN(tableNumberValue) || Number.isNaN(tableSizeValue)) {
        failureToast("Table number and size must be valid numbers");
        setLoading(false);
        return;
      }

      // Section is only for Advanced Table management (Pro); Starter sends null
      const sectionValue = hasAdvancedTableManagement
        ? (formik.values.section === "" ? null : Number(formik.values.section))
        : null;
      if (sectionValue !== null && (Number.isNaN(sectionValue) || sectionValue <= 0)) {
        failureToast("Section must be a valid number greater than 0");
        setLoading(false);
        return;
      }

      let variables: any = {};
      if (!!editTableModal) {
        variables = {
          updateStoreTableInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            storeTableCorrelationId: editTableModal?.correlationId,
            tableNumber: tableNumberValue,
            tableSize: tableSizeValue,
            section: sectionValue,
          },
        };
        delete variables?.updateStoreTableInput?.correlationId;
      } else {
        variables = {
          createStoreTableInput: {
            tableNumber: tableNumberValue,
            tableSize: tableSizeValue,
            storeCorrelationId: hasRestaurant.correlationId,
            section: sectionValue,
          },
        };
      }

      const requestPayload = {
        query: !!editTableModal ? UPDATE_TABLE : ADD_TABLE,
        variables: variables,
      };

      const { data } = await axBe.post(GRAPHQL_ENDPOINT, requestPayload);
      
      if (data?.errors?.length) {
        throw data?.errors;
      }
      
      if (!!editTableModal) {
        dispatch(edit_table(data?.data?.updateStoreTable));
      } else {
        dispatch(add_table(data?.data?.createStoreTable));
      }
      
      setLoading(false);
      setShowCreateModal();
    } catch (error) {
      console.error("Error creating user:", error);
      setLoading(false);
    }
  };

  const { setValues } = formik;

  useEffect(() => {
    if (!!editTableModal) {
      setValues({
        tableNumber: editTableModal?.tableNumber?.toString() ?? "",
        tableSize: editTableModal?.tableSize?.toString() ?? "",
        section: editTableModal?.section
          ? editTableModal.section.toString()
          : "",
      });
    }
  }, [editTableModal, setValues]);

  const isDisabled = !formik.isValid || !formik.dirty;

  return (
    <PortaledModalFrame onClose={setShowCreateModal} panelStyle={{ maxWidth: 440 }}>
      <div className="flex min-h-0 max-h-full flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#F1F1F2] px-5 py-4 sm:px-8">
          <span id="modal-title" className="text-xl font-semibold text-[#252525]">
            {title}
          </span>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={setShowCreateModal}
            aria-label="Close"
          >
            <img className="h-3 w-3" src={closeIcon} alt="" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-8">
                <div className="w-full">
                    <span className="text-[#9B9B9B] text-sm">Table Number</span>
                    <input
                      className="text-xs text-[#9B9B9B] h-[40px] w-full border rounded-lg border-[#F1F1F2] mt-1 px-2"
                      type="number"
                      name="tableNumber"
                      id="tableNumber"
                      value={formik.values.tableNumber}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="w-full mt-4">
                    <span className="text-[#9B9B9B] text-sm">Size</span>
                    <div className="mt-1">
                      <DropdownEB
                        title={"Select Size"}
                        data={TABLE_SIZE}
                        id="tableSize"
                        onSelect={(id) =>
                          formik.setFieldValue(
                            "tableSize",
                            TABLE_SIZE?.find((table) => table?.id === id)?.name
                          )
                        }
                        selectedId={
                          TABLE_SIZE?.find(
                            (table) => table?.name === formik.values.tableSize
                          )?.id
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full mt-4">
                    <span className="text-[#9B9B9B] text-sm">Section</span>
                    <input
                      id="section"
                      name="section"
                      type="number"
                      disabled={!hasAdvancedTableManagement}
                      className={`text-xs text-[#9B9B9B] h-[40px] w-full border rounded-lg border-[#F1F1F2] mt-1 px-2 focus:outline-none focus:border-[#252525] ${!hasAdvancedTableManagement ? "opacity-50 cursor-not-allowed" : ""}`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.section ?? ""}
                      min="1"
                    />
                  </div>
        </div>
        <div className="flex shrink-0 justify-end border-t border-[#F1F1F2] px-5 py-4 sm:px-8">
          {loading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              className={`flex h-9 items-center justify-center gap-2 rounded-md bg-orange px-8 text-xs text-white hover:bg-orange/90 ${
                isDisabled ? "opacity-20" : ""
              }`}
              onClick={() => manageTable()}
              disabled={isDisabled}
            >
              Confirm
            </button>
          )}
        </div>
      </div>
    </PortaledModalFrame>
  );
};